'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Divider, Checkbox, Stack } from '@mui/material';
import classnames from 'classnames';
import { useLayoutEffect, useRef, useState } from 'react';
import { formatNumber } from '@/components/cost-explorer/utils/numberUtils';
import { FILTER_ROW_HEIGHT } from '@/components/filters/filterStrip';
import { pluralize } from '@/utils/string';
import { ActionsBar, ActionsToolBar } from './actionsBar';
import { canShowPageSizeSettings, PageNavigation, PageSizeSettings, type PageSizeSource, UNKNOWN_RECORDS_COUNT } from './pageControls';
import { PaginationTitle } from './paginationTitle';
import type { TableSettingsSource } from '../table/tableSettings';
import type { AppRowSelectionApi } from '../table/tanstackTable';
const SEARCH_WIDTH = '308px';

// Gaps around the divider before the filter, plus the 40px the design keeps between the filter and the controls.
const INLINE_FILTER_SPACING = 24 + 1 + 24 + 40;
interface SharedBarProps {
  isLoading: boolean;
  entityLabel: string;
  onPageChange: (page: number) => void;
  // For callers that drive the bar themselves rather than through Table: the bar
  // renders the settings panel for them, and decides which page they land on.
  pageSizeControl?: PageSizeSource;
  /** What the table puts in its settings panel beyond the page size. */
  settings?: TableSettingsSource;
}
type TableBarProps<T extends object> = SharedBarProps & {
  variant?: 'table';
  leftAction?: React.ReactNode;
  selection?: {
    multiSelect?: {
      getIsAllRecordsSelected: AppRowSelectionApi<T>['getIsAllRowsSelected'];
      getIsSomeRecordsSelected: AppRowSelectionApi<T>['getIsSomeRowsSelected'];
      getToggleAllRecordsSelectedHandler: AppRowSelectionApi<T>['getToggleAllRowsSelectedHandler'];
      ActionButton?: React.ReactNode;
    };
    selectedRecordsCount?: number;
  };
  pagination?: {
    pageIndex: number;
    pageSize: number;
    recordsCount?: number;
    recordsLimit?: number;
    reachedLastPage?: boolean;
    displayedRangeLabel?: string;
    totalLabel?: string;
  };
  totalSourceStorageSizeBytes?: number;
  actions?: React.ReactElement[];
  leadingActions?: React.ReactElement[];
  overridePadding?: {
    left?: number | string;
    right?: number | string;
  };
};
type CompactBarProps = SharedBarProps & {
  variant: 'compact';
  // Usually a `FilterStrip`; omitted when the page has nothing to filter by.
  filter?: React.ReactNode;
  // Usually a `FreeTextFilter`; omitted when the page has no search.
  search?: React.ReactNode;
  // A sort control; omitted when the page has no sort options.
  sort?: React.ReactNode;
  pagination: {
    pageIndex: number;
    pageSize: number;
    recordsCount?: number;
    // For cursor pagination, where there is no total to tell the last page from.
    reachedLastPage?: boolean;
    // Without a total, the number of records on the current page, shown as the count.
    pageRecordsCount?: number;
  };
};

/**
 * The bar that heads a listing: its count, page navigation and settings. The `table` variant sits on a table, under
 * the page's own filter bar, and carries row selection and column actions. The `compact` variant puts the page's
 * filter, search and sort in the same row, and each of its slots renders only when the page supports it.
 */
export function PaginationBar(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = props.variant === "compact" ? <CompactBar {...props} /> : <TableBar {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function TableBar(props) {
  const $ = _c(81);
  const {
    pagination,
    selection,
    onPageChange,
    entityLabel,
    isLoading,
    leftAction,
    totalSourceStorageSizeBytes,
    actions,
    leadingActions,
    overridePadding,
    pageSizeControl,
    settings: t0
  } = props;
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const settings = t1;
  let t2;
  if ($[2] !== selection) {
    t2 = selection ?? {};
    $[2] = selection;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    multiSelect
  } = t2;
  const MultiSelectActionButton = multiSelect?.ActionButton || null;
  let t3;
  if ($[4] !== pageSizeControl || $[5] !== settings) {
    t3 = canShowPageSizeSettings(settings, pageSizeControl);
    $[4] = pageSizeControl;
    $[5] = settings;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const showSettings = t3;
  let t4;
  if ($[7] !== actions || $[8] !== showSettings) {
    t4 = (actions || []).length > 0 || showSettings;
    $[7] = actions;
    $[8] = showSettings;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const showFilteringActions = t4;
  const areSomeSelected = (selection?.selectedRecordsCount ?? 0) > 0;
  let t5;
  if ($[10] !== areSomeSelected || $[11] !== leadingActions) {
    t5 = (leadingActions || []).length > 0 && !areSomeSelected;
    $[10] = areSomeSelected;
    $[11] = leadingActions;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const showLeadingActions = t5;
  const recordsCount = pagination?.recordsCount ?? UNKNOWN_RECORDS_COUNT;
  const toCssLength = _temp;
  const t6 = overridePadding?.left ?? 28;
  let t7;
  if ($[13] !== t6) {
    t7 = toCssLength(t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const leftPadding = t7;
  const t8 = overridePadding?.right ?? 16;
  let t9;
  if ($[15] !== t8) {
    t9 = toCssLength(t8);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const rightPadding = t9;
  let t10;
  if ($[17] !== leftPadding || $[18] !== rightPadding) {
    t10 = {
      pl: leftPadding,
      pr: rightPadding
    };
    $[17] = leftPadding;
    $[18] = rightPadding;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== areSomeSelected) {
    t11 = classnames("h-full pr-[12px]", {
      "rounded-[var(--mui-shape-customBorderRadius-sm)]": areSomeSelected,
      "bg-(--mui-palette-background-paper)": areSomeSelected
    });
    $[20] = areSomeSelected;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = areSomeSelected ? "var(--mui-customShadows-panel)" : undefined;
  let t13;
  if ($[22] !== t12) {
    t13 = {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      height: "24px",
      boxShadow: t12
    };
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      alignItems: "center",
      justifyContent: "space-between",
      height: "52px",
      width: "100%"
    };
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      alignItems: "center",
      gap: "12px",
      paddingLeft: "12px"
    };
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isLoading || $[27] !== multiSelect) {
    t16 = !!multiSelect && !isLoading && <Checkbox data-testid="pagination-select-all" checked={multiSelect.getIsAllRecordsSelected() || false} indeterminate={multiSelect.getIsSomeRecordsSelected() && !multiSelect.getIsAllRecordsSelected()} onChange={multiSelect.getToggleAllRecordsSelectedHandler()} />;
    $[26] = isLoading;
    $[27] = multiSelect;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== multiSelect) {
    t17 = multiSelect?.getIsAllRecordsSelected();
    $[29] = multiSelect;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== selection || $[32] !== t17) {
    t18 = {
      ...selection,
      isAllSelected: t17
    };
    $[31] = selection;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = pagination?.displayedRangeLabel;
  let t20;
  if ($[34] !== entityLabel || $[35] !== isLoading || $[36] !== recordsCount || $[37] !== t18 || $[38] !== t19 || $[39] !== totalSourceStorageSizeBytes) {
    t20 = <PaginationTitle entityLabel={entityLabel} selection={t18} isLoading={isLoading} recordsCount={recordsCount} totalSourceStorageSizeBytes={totalSourceStorageSizeBytes} titleOverride={t19} />;
    $[34] = entityLabel;
    $[35] = isLoading;
    $[36] = recordsCount;
    $[37] = t18;
    $[38] = t19;
    $[39] = totalSourceStorageSizeBytes;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== leftAction || $[42] !== t16 || $[43] !== t20) {
    t21 = <Stack direction="row" sx={t15}>{t16}{t20}{leftAction}</Stack>;
    $[41] = leftAction;
    $[42] = t16;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = areSomeSelected && MultiSelectActionButton;
  let t23;
  if ($[45] !== t21 || $[46] !== t22) {
    t23 = <Stack direction="row" sx={t14}>{t21}{t22}</Stack>;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t11 || $[49] !== t13 || $[50] !== t23) {
    t24 = <Box className={t11} sx={t13}>{t23}</Box>;
    $[48] = t11;
    $[49] = t13;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== leadingActions || $[53] !== showLeadingActions) {
    t25 = showLeadingActions && <Stack direction="row" className="mr-[16px]" sx={{
      alignItems: "center",
      gap: "16px"
    }}>{leadingActions}</Stack>;
    $[52] = leadingActions;
    $[53] = showLeadingActions;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== areSomeSelected || $[56] !== isLoading || $[57] !== onPageChange || $[58] !== pagination || $[59] !== recordsCount || $[60] !== showLeadingActions) {
    t26 = pagination && !areSomeSelected && <PageNavigation pageIndex={pagination.pageIndex} pageSize={pagination.pageSize} recordsCount={recordsCount} recordsLimit={pagination.recordsLimit} reachedLastPage={pagination.reachedLastPage} totalLabel={pagination.totalLabel} isLoading={isLoading} onPageChange={onPageChange} labelPrefix={showLeadingActions && <Divider component="span" orientation="vertical" className="mr-[25px] h-[22px] inline-flex" />} />;
    $[55] = areSomeSelected;
    $[56] = isLoading;
    $[57] = onPageChange;
    $[58] = pagination;
    $[59] = recordsCount;
    $[60] = showLeadingActions;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== actions || $[63] !== areSomeSelected || $[64] !== pageSizeControl || $[65] !== pagination?.pageIndex || $[66] !== recordsCount || $[67] !== settings || $[68] !== showFilteringActions || $[69] !== showSettings) {
    t27 = showFilteringActions && <>{areSomeSelected ? <div className="my-[15px] mr-[16px]" /> : <Divider orientation="vertical" flexItem={true} variant="middle" sx={{
        marginY: "18px",
        marginX: "24px"
      }} />}<Stack direction="row" sx={{
        paddingRight: "12px",
        alignItems: "center",
        gap: "12px"
      }}>{actions}{showSettings && pageSizeControl && <PageSizeSettings settings={settings} pageSizeControl={pageSizeControl} pageIndex={pagination?.pageIndex ?? 0} recordsCount={recordsCount} />}</Stack></>;
    $[62] = actions;
    $[63] = areSomeSelected;
    $[64] = pageSizeControl;
    $[65] = pagination?.pageIndex;
    $[66] = recordsCount;
    $[67] = settings;
    $[68] = showFilteringActions;
    $[69] = showSettings;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t25 || $[72] !== t26 || $[73] !== t27) {
    t28 = <>{t25}{t26}{t27}</>;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== t24 || $[76] !== t28) {
    t29 = <ActionsToolBar disableGutters={true}>{t24}{t28}</ActionsToolBar>;
    $[75] = t24;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t10 || $[79] !== t29) {
    t30 = <ActionsBar position="static" className="mb-0 py-[8px]" sx={t10}>{t29}</ActionsBar>;
    $[78] = t10;
    $[79] = t29;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  return t30;
}
function _temp(v) {
  return typeof v === "number" ? `${v}px` : v;
}
const CompactBar = t0 => {
  const $ = _c(46);
  const {
    entityLabel,
    isLoading,
    filter,
    search,
    sort,
    pagination,
    onPageChange,
    pageSizeControl,
    settings: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const settings = t2;
  const pageStart = pagination.pageIndex * pagination.pageSize;
  const {
    pageRecordsCount
  } = pagination;
  const knownTotal = pagination.recordsCount ?? (pagination.reachedLastPage && pageRecordsCount !== undefined ? pageStart + pageRecordsCount : undefined);
  const recordsCount = knownTotal ?? UNKNOWN_RECORDS_COUNT;
  const countedRecords = knownTotal ?? pageRecordsCount;
  const toolbarRef = useRef(null);
  const countRef = useRef(null);
  const filterRef = useRef(null);
  const controlsRef = useRef(null);
  const [isFilterBelow, setIsFilterBelow] = useState(false);
  const hasFilter = !!filter;
  let t3;
  let t4;
  if ($[2] !== hasFilter) {
    t3 = () => {
      const toolbar = toolbarRef.current;
      const count = countRef.current;
      const filterBlock = filterRef.current;
      const controls = controlsRef.current;
      if (!hasFilter || !toolbar || !count || !filterBlock || !controls) {
        return;
      }
      const measure = () => {
        const available = controls.getBoundingClientRect().left - count.getBoundingClientRect().right - INLINE_FILTER_SPACING;
        setIsFilterBelow(filterBlock.offsetWidth > available);
      };
      measure();
      const observer = new ResizeObserver(measure);
      [toolbar, count, filterBlock, controls].forEach(el => observer.observe(el));
      return () => observer.disconnect();
    };
    t4 = [hasFilter];
    $[2] = hasFilter;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      padding: "16px 40px",
      columnGap: "24px",
      rowGap: "24px",
      flexWrap: "wrap",
      alignItems: "center",
      borderTop: "1px solid var(--mui-palette-divider)",
      borderBottom: "1px solid var(--mui-palette-divider)"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      flexShrink: 0,
      justifyContent: "center",
      minHeight: FILTER_ROW_HEIGHT
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== countedRecords || $[8] !== entityLabel) {
    t7 = countedRecords === undefined ? undefined : `${formatNumber(countedRecords, 0)} ${pluralize(entityLabel, countedRecords)}`;
    $[7] = countedRecords;
    $[8] = entityLabel;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== entityLabel || $[11] !== isLoading || $[12] !== recordsCount || $[13] !== t7) {
    t8 = <Stack ref={countRef} sx={t6}><PaginationTitle entityLabel={entityLabel} isLoading={isLoading} recordsCount={recordsCount} titleOverride={t7} /></Stack>;
    $[10] = entityLabel;
    $[11] = isLoading;
    $[12] = recordsCount;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== filter || $[16] !== isFilterBelow) {
    t9 = filter && <Stack data-testid="compact-toolbar-filter" direction="row" sx={isFilterBelow ? {
      order: 2,
      flexBasis: "100%"
    } : {
      flex: 1,
      minWidth: 0,
      alignItems: "center",
      gap: "24px",
      paddingRight: "16px"
    }}>{!isFilterBelow && <Divider orientation="vertical" sx={{
        height: "16px"
      }} />}<Box ref={filterRef} sx={{
        width: "max-content",
        flexShrink: 0,
        maxWidth: isFilterBelow ? "100%" : undefined
      }}>{filter}</Box></Stack>;
    $[15] = filter;
    $[16] = isFilterBelow;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      flexShrink: 0,
      marginLeft: "auto",
      alignItems: "center",
      gap: "24px",
      minHeight: FILTER_ROW_HEIGHT
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== search) {
    t11 = search && <Box sx={{
      width: SEARCH_WIDTH
    }}>{search}</Box>;
    $[19] = search;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = knownTotal === undefined && pageRecordsCount !== undefined ? pageStart + pageRecordsCount : undefined;
  let t13;
  if ($[21] !== isLoading || $[22] !== onPageChange || $[23] !== pagination.pageIndex || $[24] !== pagination.pageSize || $[25] !== pagination.reachedLastPage || $[26] !== recordsCount || $[27] !== t12) {
    t13 = <PageNavigation pageIndex={pagination.pageIndex} pageSize={pagination.pageSize} recordsCount={recordsCount} reachedLastPage={pagination.reachedLastPage} rangeEnd={t12} isLoading={isLoading} onPageChange={onPageChange} minHeight={FILTER_ROW_HEIGHT} />;
    $[21] = isLoading;
    $[22] = onPageChange;
    $[23] = pagination.pageIndex;
    $[24] = pagination.pageSize;
    $[25] = pagination.reachedLastPage;
    $[26] = recordsCount;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== pageSizeControl || $[30] !== pagination.pageIndex || $[31] !== recordsCount || $[32] !== settings) {
    t14 = pageSizeControl && canShowPageSizeSettings(settings, pageSizeControl) && <PageSizeSettings settings={settings} pageSizeControl={pageSizeControl} pageIndex={pagination.pageIndex} recordsCount={recordsCount} />;
    $[29] = pageSizeControl;
    $[30] = pagination.pageIndex;
    $[31] = recordsCount;
    $[32] = settings;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== sort || $[35] !== t11 || $[36] !== t13 || $[37] !== t14) {
    t15 = <Stack ref={controlsRef} direction="row" sx={t10}>{t11}{sort}{t13}{t14}</Stack>;
    $[34] = sort;
    $[35] = t11;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== isFilterBelow) {
    t16 = isFilterBelow && <Divider data-testid="compact-toolbar-row-divider" sx={{
      order: 1,
      flexBasis: "100%",
      borderStyle: "dashed"
    }} />;
    $[39] = isFilterBelow;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t15 || $[42] !== t16 || $[43] !== t8 || $[44] !== t9) {
    t17 = <Stack ref={toolbarRef} data-testid="compact-toolbar" direction="row" sx={t5}>{t8}{t9}{t15}{t16}</Stack>;
    $[41] = t15;
    $[42] = t16;
    $[43] = t8;
    $[44] = t9;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
};